// Tech Support Co-Pilot — reply-template picker modal.
//
// John's 18 house-standard canned replies (services/email/reply_templates.py),
// served by GET /tech-support-copilot/templates. A pure SELECTION modal: picking
// one hands the template back to the caller (the composer), which inserts its
// body AT THE CURSOR in the message being edited — like a mail client's canned
// responses. No server-side insertion: the composer's normal Save draft / Send
// persist the result, so John controls exactly where the language lands.
// Placeholders like {name}/{state}/{product} are left for John to fill.
// The agent uses this same set via its playbook, so the manual picker and the
// agent stay in lock-step.

import { useEffect, useMemo, useState } from 'react';
import {
  listTemplates,
  type ReplyTemplate,
} from '../../services/techSupportCopilot';
import styles from './TechSupportTemplatePicker.module.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the chosen template — the caller inserts it where the cursor is. */
  onPick: (template: ReplyTemplate) => void;
}

/** Friendly label for each template category chip. */
const CATEGORY_LABEL: Record<string, string> = {
  referral_rep: 'Rep referral',
  referral_distributor: 'Distributor',
  standard: 'Standard',
  application: 'Application',
};

export default function TechSupportTemplatePicker({ isOpen, onClose, onPick }: Props) {
  // The dialog mounts fresh on each open, so search/selection reset for free
  // (no state-sync effects) and the tiny template list is re-fetched per open.
  if (!isOpen) return null;
  return <PickerDialog onClose={onClose} onPick={onPick} />;
}

function PickerDialog({ onClose, onPick }: Omit<Props, 'isOpen'>) {
  // null = still loading; loading state is derived, never set synchronously.
  const [templates, setTemplates] = useState<ReplyTemplate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listTemplates()
      .then((t) => {
        if (!cancelled) setTemplates(t);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the templates — please try again.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = templates === null && !loadError;

  const filtered = useMemo(() => {
    const all = templates ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.when_to_use.toLowerCase().includes(q) ||
        t.body.toLowerCase().includes(q),
    );
  }, [templates, query]);

  const selected = useMemo(
    () => (templates ?? []).find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  );

  const insert = () => {
    if (!selected) return;
    onPick(selected);
    onClose();
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Insert a reply template"
      onClick={onClose}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.head}>
          <span className={styles.title}>📋 Reply templates</span>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <p className={styles.note}>
          Pick one of John&apos;s house-standard replies — it&apos;s inserted where your
          cursor is, so you control exactly where it lands. {'{name}'} is filled in
          from the email&apos;s sender; anything else in {'{braces}'} is yours to
          fill. The co-pilot uses the same set.
        </p>

        <input
          className={styles.search}
          type="search"
          placeholder="Search templates…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search templates"
        />

        <div className={styles.cols}>
          <div className={styles.list} role="listbox" aria-label="Templates">
            {loading && <div className={styles.muted}>Loading templates…</div>}
            {loadError && <div className={styles.error}>{loadError}</div>}
            {!loading && !loadError && filtered.length === 0 && (
              <div className={styles.muted}>No templates match that search.</div>
            )}
            {filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                role="option"
                aria-selected={t.id === selectedId}
                className={`${styles.row} ${t.id === selectedId ? styles.rowOn : ''}`}
                onClick={() => setSelectedId(t.id)}
              >
                <span className={styles.rowTop}>
                  <span className={styles.rowName}>{t.name}</span>
                  <span className={styles.chip}>
                    {CATEGORY_LABEL[t.category] ?? t.category}
                  </span>
                </span>
                <span className={styles.rowWhen}>{t.when_to_use}</span>
              </button>
            ))}
          </div>

          <div className={styles.preview}>
            {selected ? (
              <>
                <div className={styles.previewName}>{selected.name}</div>
                <div className={styles.previewWhen}>{selected.when_to_use}</div>
                <pre className={styles.previewBody}>{selected.body}</pre>
              </>
            ) : (
              <div className={styles.muted}>Select a template on the left to preview it.</div>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.insert}
            onClick={insert}
            disabled={!selected}
          >
            Insert at cursor
          </button>
        </div>
      </div>
    </div>
  );
}
